// @ts-strict-ignore
import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  HostBinding, HostListener,
  Input,
  OnInit,
  Output, ViewChild
} from '@angular/core';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMListUtilService } from '@list/services/lm-list-util.service';
import { SohoCheckBoxComponent } from 'ids-enterprise-ng';

@Component({
  selector: 'lm-block-item',
  standalone: false,
  templateUrl: './lm-block-item.component.html',
  styleUrls:   [ 'lm-block-item.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMBlockItemComponent implements OnInit {
  @Input() selected: boolean | undefined;
  @Input() isSelectable = true;
  @Input() content: 'image-item' | 'sectioned-card';
  @Input() populateTooltip: boolean;
  @Input() blockGridOptions: BlockGridOptions = null;
  @Input() isHorizontal: boolean;
  @Input() automationId: string;
  @Input() index: number;
  @Input() set data(imageData: BlockGridData) {
    this._data = imageData;

    if (this.populateTooltip) {
      this._populateTooltip();
    }
  }

  @HostBinding('attr.data-automation-id') public blockItemAutomationId: string;
  @HostBinding('style.width.px') @Input() width: string;
  @HostBinding('style.margin') @Input() margin = '15px 0 0 15px';

  @HostBinding('class') get hostClassName() {
    return this.content;
  }

  @HostListener('mouseover') onMouseOver() {
    this.showCheckBox();
  }

  @HostListener('mouseout') onMouseOut() {
    this.hideCheckBox();
  }

  @Output() itemSelected = new EventEmitter<LMBlockGridItemEvent>();
  @Output() itemActivated = new EventEmitter<LMBlockGridItemEvent>();
  @Output() itemUnSelected = new EventEmitter<LMBlockGridItemEvent>();
  @Output() itemDoubleClicked = new EventEmitter<LMBlockGridItemEvent>();
  @Output() contextMenu = new EventEmitter<LMBlockGridItemEvent>();

  @ViewChild('Checkbox') checkbox: SohoCheckBoxComponent;
  @ViewChild('Block') block: ElementRef;

  public tooltip: string;
  public checkboxIsVisible = false;
  public blockItemCheckboxAutomationId: string;

  private _data: BlockGridData;

  constructor(
    protected lmFormatUtilService: LMFormatUtilService,
    private lmListUtilService: LMListUtilService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.blockItemAutomationId = `lm-${this.automationId}-blockitem-${this.index}`;
    this.blockItemCheckboxAutomationId = `lm-${this.automationId}-blockitem-checkbox-${this.index}`;
  }

  public selectItem(event: Event) {
    if (!this.isSelectable && this.targetIsActionable(event)) {
      return;
    }

    // Set timeout to allow double click time to be captured before single click
    setTimeout(() => {
      const blockGridItemEvent: LMBlockGridItemEvent = {
        cell: -1,
        item: this,
        row: this.data.dvIndex,
        originalEvent: null,
        type: null
      };

      this.check();
      this.itemSelected.emit(blockGridItemEvent);
    });
  }

  public activateRow(event) {
    if (!this.isSelectable && this.targetIsActionable(event)) {
      return;
    }

    const blockGridItemEvent: LMBlockGridItemEvent = {
      cell: -1,
      item: this,
      row: this.data.dvIndex,
      originalEvent: null,
      type: null
    };

    this.itemActivated.emit(blockGridItemEvent);
  }

  public unSelectItem(event: Event) {
    if (!this.isSelectable && this.targetIsActionable(event)) {
      return;
    }

    const blockGridItemEvent: LMBlockGridItemEvent = {
      cell: -1,
      item: this,
      row: this.data.dvIndex,
      originalEvent: null,
      type: null
    };

    this.unCheck();
    this.itemUnSelected.emit(blockGridItemEvent);

    // Manually remove selection, make block grid selection behave like other list implementations
    setTimeout(() => {
      this.block.nativeElement.classList.remove('is-selected');
    }, 50);
  }

  public doubleClick() {
    const blockGridItemEvent: LMBlockGridItemEvent = {
      cell: -1,
      item: this,
      row: this.data.dvIndex,
      originalEvent: null,
      type: null
    };

    this.itemDoubleClicked.emit(blockGridItemEvent);
  }

  public onContextMenu(e?: JQuery.TriggeredEvent) {
    e.preventDefault();

    this.selected = true;

    const blockGridItemEvent: LMBlockGridItemEvent = {
      cell: -1,
      item: this,
      row: this.data.dvIndex,
      originalEvent: e,
      type: null
    };

    this.contextMenu.emit(blockGridItemEvent);
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public get data(): BlockGridData {
    return this._data;
  }

  public showCheckBox() {
    this.checkboxIsVisible = true;
  }

  public hideCheckBox() {
    this.checkboxIsVisible = this.selected;
  }

  public check() {
    this.selected = true;
    this.changeDetectorRef.markForCheck();
  }

  public unCheck() {
    if (this.selected) {
      this.selected = undefined;
      this.changeDetectorRef.markForCheck();
    }
  }

  private _populateTooltip() {
    if (this._data && this._data.detailFields) {
      this.tooltip = this.lmListUtilService.buildDetailFieldsAsTooltip(this._data.dataView, this._data.detailFields);
    }
  }

  private targetIsActionable(event) {
    const isHyperlink = event.target.classList.contains('hyperlink');
    const isButton = event.target.classList.contains('btn');

    return isHyperlink || isButton;
  }
}
